import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { UserRole } from "@/generated/types";
import { useInviteUser } from "../../../hooks/useInviteUser";

interface InviteUserDialogProps {
  onSuccess: () => void;
  children: React.ReactNode;
}

const ROLE_LABELS: Record<string, string> = {
  ADMIN: "Administrador",
  CLIENT: "Cliente",
  COLLABORATOR: "Colaborador",
};

export default function InviteUserDialog({ onSuccess, children }: InviteUserDialogProps) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<string>(UserRole.Client);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const { inviteUser, loading } = useInviteUser({
    onCompleted: () => {
      toast.success("Convite enviado com sucesso.");
      setEmail("");
      setRole(UserRole.Client);
      setFormErrors({});
      onSuccess();
    },
  });

  const validate = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    if (!email.trim()) {
      errors.email = "E-mail é obrigatório";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Por favor, insira um endereço de e-mail válido";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [email]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    inviteUser(email, role);
  }, [validate, inviteUser, email, role]);

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent maxWidth="max-w-lg">
        <DialogHeader>
          <DialogTitle>Convidar Usuário</DialogTitle>
          <DialogDescription>
            Enviar um e-mail de convite. O usuário criará sua própria conta.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          <div className="space-y-2">
            <Label htmlFor="invite-email" className="text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400">
              E-mail
            </Label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-none border-primary-200 dark:border-primary-800"
            />
            {formErrors.email && <p className="text-xs text-red-600 dark:text-red-400">{formErrors.email}</p>}
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400">
              Função
            </Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="rounded-none border-primary-200 dark:border-primary-800">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(UserRole).map((r) => (
                  <SelectItem key={r} value={r}>
                    {ROLE_LABELS[r] || r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button
                type="button"
                variant="outline"
                className="rounded-none uppercase tracking-widest text-xs border-primary-200 dark:border-primary-800"
              >
                Cancelar
              </Button>
            </DialogClose>
            <Button
              type="submit"
              disabled={loading}
              className="rounded-none uppercase tracking-widest text-xs"
            >
              {loading ? "Enviando..." : "Enviar Convite"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
